import { ArrowLeft, Plus, MoreVertical, Crown, CheckCircle2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { apiGet, apiPost, apiDelete } from '../utils/apiClient';
import { InviteFamilyDrawer } from './InviteFamilyDrawer';
import { RemoveMemberDrawer } from './RemoveMemberDrawer';

export function FamilySettings({ onBack }) {
  const [isInviteDrawerOpen, setIsInviteDrawerOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [resendingInviteId, setResendingInviteId] = useState('');

  const [household, setHousehold] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const members = Array.isArray(household?.members) ? household.members : [];
  const pendingInvitations = Array.isArray(household?.pendingInvites)
    ? household.pendingInvites.filter((invite) => invite?.type !== 'share-link' && invite?.email)
    : (Array.isArray(household?.pendingInvitations) ? household.pendingInvitations : []);
  const maxMembers = household?.maxMembers || 4;
  // Seats the owner can fill. The cap counts the owner, so invitable seats are one fewer, and
  // a pending invitation holds a seat just as a member does.
  const inviteCapacity = Math.max(0, maxMembers - 1);
  const invitedCount = Math.max(0, members.length - 1) + pendingInvitations.length;
  // `household.plan` is populated by GET /households/me from the OWNER's record — the badge
  // describes the household's plan, not the viewer's. This previously read
  // `household.subscriptionStatus`, a field the API never returned, so the `|| 'active'`
  // fallback made the badge say "Active" unconditionally.
  const plan = household?.plan || null;
  const planActive = plan ? plan.active : null;
  const ownerUid = household?.ownerUid;
  const ownerFirstName = (members.find((member) => member?.uid === ownerUid || member?.role === 'owner')?.name || '').trim().split(/\s+/)[0] || '';

  // `silent` revalidates without flipping the full-screen loading state. The invite drawer
  // refreshes seat counts as it opens, and the loading screen replaces this whole component —
  // which would unmount the drawer mid-open and throw away what the owner had typed.
  const refreshHousehold = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const data = await apiGet('/households/me');
      setHousehold(data);
    } catch (err) {
      console.warn('Failed to load household', err);
      // A failed background refresh must not blank the screen the owner is already using.
      if (!silent) setError('Unable to load household right now.');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    refreshHousehold();
  }, []);

  if (loading) {
    return (
      <div className="mpg-focus-scope min-h-screen bg-[#F8F7F5] flex flex-col">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="px-6 py-4 flex items-center gap-3">
            <button
              onClick={onBack}
              className="w-11 h-11 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
              aria-label="Back"
            >
              <ArrowLeft className="w-5 h-5 text-gray-900" />
            </button>
            <h1 className="text-xl flex-1">Family & Sharing</h1>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center text-[#757575]">Loading household…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mpg-focus-scope min-h-screen bg-[#F8F7F5] flex flex-col">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="px-6 py-4 flex items-center gap-3">
            <button
              onClick={onBack}
              className="w-11 h-11 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
              aria-label="Back"
            >
              <ArrowLeft className="w-5 h-5 text-gray-900" />
            </button>
            <h1 className="text-xl flex-1">Family & Sharing</h1>
          </div>
        </header>
        <div className="flex-1 flex flex-col gap-3 items-center justify-center text-[#757575] px-6 text-center">
          <p>{error}</p>
          <button
            onClick={() => refreshHousehold()}
            className="px-4 py-2 bg-[#AFBB8B] text-white rounded-full hover:bg-[#9FAA7B] transition-colors"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const handleInviteSent = async (email, firstName, message) => {
    setActionError('');
    setSuccessMessage('');
    try {
      const data = await apiPost('/households/invitations', { email, firstName, message });
      setHousehold(data);
      setIsInviteDrawerOpen(false);
      setSuccessMessage('Invitation sent.');
    } catch (err) {
      console.warn('Invite failed', err);
      // Rethrown rather than written to `actionError`: that banner renders on THIS screen,
      // which sits behind the open drawer, so the owner never saw why the send failed. The
      // drawer catches this and shows it above its own form.
      throw err;
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      setActionError('');
      setSuccessMessage('');
      const data = await apiDelete(`/households/members/${memberId}`);
      setHousehold(data);
      setMemberToRemove(null);
      setSuccessMessage('Member removed.');
    } catch (err) {
      console.warn('Remove member failed', err);
      setActionError(err?.message || 'Could not remove member');
    }
  };

  const handleCancelInvitation = async (inviteId) => {
    try {
      setActionError('');
      setSuccessMessage('');
      const data = await apiDelete(`/households/invitations/${inviteId}`);
      setHousehold(data);
      setSuccessMessage('Invitation cancelled.');
    } catch (err) {
      console.warn('Cancel invite failed', err);
      setActionError(err?.message || 'Could not cancel invite');
    }
  };

  const handleResendInvitation = async (inviteId) => {
    try {
      setResendingInviteId(inviteId);
      setActionError('');
      setSuccessMessage('');
      const data = await apiPost(`/households/invitations/${inviteId}/resend`, {});
      setHousehold(data);
      setSuccessMessage('Invitation resent.');
    } catch (err) {
      console.warn('Resend invite failed', err);
      setActionError(err?.message || 'Could not resend invite');
    } finally {
      setResendingInviteId('');
    }
  };

  return (
    <div className="mpg-focus-scope min-h-screen bg-[#F8F7F5]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-6 py-4 flex items-center gap-3">
          <button
            onClick={onBack}
            className="w-11 h-11 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5 text-gray-900" />
          </button>
          <h1 className="text-xl flex-1">Family & Sharing</h1>
        </div>
      </header>

      {/* Content */}
      <div className="px-6 py-6 pb-24 max-w-2xl mx-auto">
        {successMessage && (
          <div className="mb-4 rounded-2xl border border-[#BFDCCB] bg-[#EAF6EE] px-4 py-3 text-sm text-[#1F5D38]" role="status" aria-live="polite">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#1F5D38]" />
              <p>{successMessage}</p>
            </div>
          </div>
        )}

        {actionError && (
          <div className="mb-4 rounded-2xl border border-[#D4183D] bg-[#FEF3F2] px-4 py-3 text-sm text-[#B42318]" role="alert">
            <div className="flex items-start gap-2">
              <span className="mt-0.5 font-bold" aria-hidden="true">!</span>
              <p>{actionError}</p>
            </div>
          </div>
        )}

        {/* Household Info */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-lg mb-1">Your Household</h2>
              <p className="text-sm text-[#757575]">
                {members.length} of {maxMembers} members
              </p>
            </div>
            <div className="px-3 py-1 bg-[#E8F0ED] text-[#5F6B3F] font-medium rounded-full text-xs">
              {planActive === null ? '—' : planActive ? 'Active' : 'Inactive'}
            </div>
          </div>

          <p className="text-sm text-[#757575] mb-4">
            Everyone in your household can view and edit meal plans, shopping lists, and recipes together.
          </p>

          <button
            onClick={() => {
              setActionError('');
              setSuccessMessage('');
              setIsInviteDrawerOpen(true);
            }}
            className="w-full min-h-[48px] py-3 bg-[#AFBB8B] text-[#1F2814] font-medium rounded-full hover:bg-[#9AA876] transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="w-5 h-5" />
            <span>Invite Family Member</span>
          </button>
        </div>

        {/* Household Members */}
        <div className="mb-6">
          <h3 className="text-lg mb-3 px-1">Household Members</h3>
          <div className="space-y-3">
            {members.map((member) => {
              const memberId = member.uid || member.id;
              const displayName = member.name || member.email?.split('@')[0] || memberId;
              const displayEmail = member.email || '';
              const joined = member.joinedAt
                ? new Date(member.joinedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : '';
              const isOwner = member.role === 'owner' || (!!ownerUid && (member.uid === ownerUid || memberId === ownerUid));
              const enriched = { ...member, id: memberId, name: displayName };
              return (
              <div
                key={memberId}
                className="bg-white rounded-2xl shadow-sm p-4 flex items-center gap-4"
              >
                {/* Avatar */}
                <div className="w-12 h-12 rounded-full bg-[#E8F0ED] flex items-center justify-center text-[#5F6B3F] text-lg">
                  {displayName.charAt(0).toUpperCase()}
                </div>

                {/* Member Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900">
                      {displayName}
                      {member.isCurrentUser && (
                        <span className="text-[#757575] font-normal"> (You)</span>
                      )}
                    </span>
                    {isOwner && (
                      <Crown className="w-4 h-4 text-[#DFA79E]" />
                    )}
                  </div>
                  <p className="text-sm text-[#757575]">{displayEmail}</p>
                  <p className="text-xs text-[#757575] mt-1">
                    {isOwner ? 'Owner • Subscription holder' : `Member${joined ? ` • Joined ${joined}` : ''}`}
                  </p>
                </div>

                {/* Actions */}
                {!member.isCurrentUser && !isOwner && (
                  <button
                    onClick={() => setMemberToRemove(enriched)}
                    className="w-11 h-11 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                    aria-label="Remove member"
                  >
                    <MoreVertical className="w-5 h-5 text-[#757575]" />
                  </button>
                )}
              </div>
            );
            })}
          </div>
        </div>

        {/* Pending Invitations */}
        {pendingInvitations.length > 0 && (
          <div>
            <h3 className="text-lg mb-3 px-1">Pending Invitations</h3>
            <div className="space-y-3">
              {pendingInvitations.map((invite) => (
                <div
                  key={invite.id}
                  className="bg-white rounded-2xl shadow-sm p-4 flex flex-col sm:flex-row sm:items-center gap-4"
                >
                  {/* Avatar + Info group */}
                  <div className="flex items-start gap-4 flex-1 min-w-0">
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-[#757575] text-lg flex-shrink-0">
                      {invite.email.charAt(0).toUpperCase()}
                    </div>

                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 mb-1">{invite.email}</p>
                      <p className="text-xs text-[#757575]">
                        Invited by {invite.invitedBy || 'Owner'} on {invite.sentAt ? new Date(invite.sentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                      </p>
                    </div>
                  </div>

                  {/* Buttons group */}
                  <div className="flex flex-wrap items-center gap-2 sm:flex-shrink-0">
                    <button
                      onClick={() => handleResendInvitation(invite.id)}
                      disabled={resendingInviteId === invite.id}
                      className="px-4 py-2 text-sm text-[#1F2814] bg-[#AFBB8B] hover:bg-[#9FAA7B] rounded-full transition-colors disabled:opacity-60"
                    >
                      {resendingInviteId === invite.id ? 'Resending...' : 'Resend'}
                    </button>
                    <button
                      onClick={() => handleCancelInvitation(invite.id)}
                      className="px-4 py-2 text-sm text-[#B85A5A] hover:bg-[#FEF5F5] rounded-full transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* How it works */}
        <div className="mt-8 bg-[#F0F3EC] rounded-2xl p-6">
          <h3 className="text-lg mb-3">How it works</h3>
          <ul className="space-y-2 text-sm text-[#5C5F6A]">
            <li className="flex gap-2">
              <span>•</span>
              <span>One subscription covers your whole household</span>
            </li>
            <li className="flex gap-2">
              <span>•</span>
              <span>Everyone shares meal plans, shopping lists, and recipes</span>
            </li>
            <li className="flex gap-2">
              <span>•</span>
              <span>Each person has their own login for personal notifications</span>
            </li>
            <li className="flex gap-2">
              <span>•</span>
              <span>Invite up to {household.maxMembers} family members</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Invite Drawer */}
      <InviteFamilyDrawer
        isOpen={isInviteDrawerOpen}
        onClose={() => setIsInviteDrawerOpen(false)}
        onInvite={handleInviteSent}
        onOpen={() => refreshHousehold({ silent: true })}
        invitedCount={invitedCount}
        inviteCapacity={inviteCapacity}
        pendingCount={pendingInvitations.length}
        ownerFirstName={ownerFirstName}
      />

      {/* Remove Member Drawer */}
      {memberToRemove && (
        <RemoveMemberDrawer
          isOpen={!!memberToRemove}
          onClose={() => setMemberToRemove(null)}
          member={memberToRemove}
          onConfirm={handleRemoveMember}
        />
      )}
    </div>
  );
}
